import { UserType } from '../generated/graphql';

import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useRef } from 'react';

import { isAbortedRequestError } from '../apollo';
import { getVisitorPresenceUpdateIntervalMs } from '../config';
import { logger } from '../datadog/logger';
import { roomAtom } from '../video-sdk';
import { useGetCurrentProviderQuery } from './useGetCurrentProviderQuery';
import { useRecordUnaccompaniedParticipantMutation } from './useRecordUnaccompaniedParticipantMutation';

export function useRecordUnaccompaniedProviderInterval() {
  const [recordUnaccompaniedParticipant] = useRecordUnaccompaniedParticipantMutation();

  const { data } = useGetCurrentProviderQuery();
  const providerShortId = data?.currentProvider.shortId;
  const providerName = data?.currentProvider.name;

  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(
    function continuouslyUpdatePatientPresence() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      const recordUnaccompaniedParticipantCallback = async () => {
        if (!providerShortId || !providerName) {
          return;
        }
        // If a room exists, then a patient has already joined
        if (getRoom()) {
          return;
        }

        try {
          recordUnaccompaniedParticipant({
            variables: {
              entityShortId: providerShortId,
              entityType: UserType.Provider,
              name: providerName,
            },
            context: {
              skipAuth: true,
            },
          });
        } catch (e) {
          const error = e as Error;
          if (isAbortedRequestError(error)) {
            logger.info('Aborted recording unaccompanied provider', { error });
            return;
          }
          logger.error('Unable to record unaccompanied provider', {}, error);
        }
      };
      recordUnaccompaniedParticipantCallback();
      intervalRef.current = setInterval(recordUnaccompaniedParticipantCallback, getVisitorPresenceUpdateIntervalMs());

      return function cleanup() {
        clearInterval(intervalRef.current);
      };
    },
    [providerShortId, providerName, getRoom, recordUnaccompaniedParticipant],
  );
}
